export function Sparkline({ data, color = "#6366f1", width = 80, height = 28 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data
    .map(
      (v, i) =>
        `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`,
    )
    .join(" ");
  return (
    <svg width={width} height={height}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function BarChart({ data, height = 180, color = "#6366f1" }) {
  if (!data || data.length === 0) return null;

  // Coerce values so missing/null counts don't poison Math.max.
  const cleaned = data.map((d) => ({ ...d, value: Number(d.value) || 0 }));
  const max = Math.max(...cleaned.map((d) => d.value), 1);
  const allZero = cleaned.every((d) => d.value === 0);

  return (
    <div style={{ position: "relative", width: "100%", height, padding: "8px 0" }}>
      {allZero && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--ink-400)",
            fontSize: 12,
            pointerEvents: "none",
          }}
        >
          No data yet
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 8,
          width: "100%",
          height: "100%",
          opacity: allZero ? 0.35 : 1,
        }}
      >
        {cleaned.map((d, i) => {
          const pct = (d.value / max) * 100;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              {/* The flex:1 child guarantees the inner column has a real height
                  for the bar's % to resolve against. */}
              <div
                style={{
                  width: "100%",
                  flex: 1,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "70%",
                    height: `${Math.max(2, pct)}%`,
                    background: d.color || color,
                    borderRadius: "4px 4px 0 0",
                    transition: "height 0.2s ease",
                  }}
                  title={`${d.label}: ${d.value}`}
                />
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontSize: 10,
                  color: "var(--ink-500)",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {d.label}
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-800)", marginTop: 2 }}>
                  {d.value}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Donut({ segments, size = 120, thickness = 18 }) {
  const cleaned = (segments || []).map((s) => ({ ...s, value: Number(s.value) || 0 }));
  const total = cleaned.reduce((s, x) => s + x.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const circ = 2 * Math.PI * radius;
  let offset = 0;
  const allZero = cleaned.every((s) => s.value === 0);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--surface-2)" strokeWidth={thickness} />
      {!allZero && cleaned.map((s, i) => {
        const len = (s.value / total) * circ;
        const dasharray = `${len} ${circ}`;
        const dashoffset = -offset;
        offset += len;
        return (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={s.color}
            strokeWidth={thickness}
            strokeDasharray={dasharray}
            strokeDashoffset={dashoffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            strokeLinecap="butt"
          />
        );
      })}
      <text x={size / 2} y={size / 2 - 2} textAnchor="middle" fontSize="22" fontWeight="700" fill="var(--ink-900)">
        {cleaned.reduce((s, x) => s + x.value, 0)}
      </text>
      <text x={size / 2} y={size / 2 + 14} textAnchor="middle" fontSize="10" fill="var(--ink-500)" letterSpacing="0.5">
        TOTAL
      </text>
    </svg>
  );
}
